import { ShieldCheck } from "lucide-react";
import { useEffect } from "react";

/**
 * "Verified by CMHO" trust badge — renders ONLY when the organization has
 * completed SuperAdmin KYC review (kyc_status='approved' + is_active=True +
 * status='active'). The backend collapses these three checks into the
 * boolean `cmho_verified` returned on /api/public/hospital/{slug} and
 * /api/public/dr/{slug} → so consumers just need to pass that flag in.
 *
 * Variants
 *   - hero  : big pill for the top of a profile card
 *   - inline: compact chip for search cards / directory rows
 *   - solid : dark version for gradient hero panels
 */
export default function VerifiedBadge({
  verified,
  variant = "hero",
  className = "",
  showTooltip = true,
  verifiedAt,
}) {
  if (!verified) return null;

  const base =
    "inline-flex items-center gap-1.5 font-semibold whitespace-nowrap select-none " +
    "transition-all duration-200 ease-out";
  const styles = {
    hero:
      "px-3 py-1.5 rounded-full text-sm bg-emerald-50 text-emerald-800 " +
      "border border-emerald-300 shadow-sm hover:shadow-md hover:-translate-y-0.5",
    inline:
      "px-2 py-0.5 rounded-full text-[11px] bg-emerald-100 text-emerald-800 " +
      "border border-emerald-200",
    solid:
      "px-3 py-1.5 rounded-full text-sm bg-white/15 text-white " +
      "border border-white/30 backdrop-blur hover:bg-white/25",
  };
  const iconSize = variant === "inline" ? "w-3 h-3" : "w-4 h-4";

  const dateStr = verifiedAt
    ? new Date(verifiedAt).toLocaleDateString("en-IN", { month: "short", year: "numeric" })
    : null;
  const tooltip = showTooltip
    ? `Verified by CMHO${dateStr ? ` · ${dateStr}` : ""} · UpchaarQ Trust Program`
    : undefined;

  return (
    <span
      className={`${base} ${styles[variant]} ${className}`}
      title={tooltip}
      data-testid="cmho-verified-badge"
      aria-label="Verified by CMHO"
    >
      <ShieldCheck className={`${iconSize} shrink-0`} strokeWidth={2.5} />
      <span>{variant === "inline" ? "CMHO Verified" : "Verified by CMHO"}</span>
    </span>
  );
}

/**
 * <VerifiedSeoMeta /> — injects the machine-readable schema.org meta tags so
 * search crawlers and social share cards surface the trust status. Uses raw
 * DOM mutation (no react-helmet dependency).
 */
export function useVerifiedSeoMeta({ verified, entityName, verifiedAt, url }) {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const upsert = (name, value) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    upsert("upchaar:verified", verified ? "true" : "false");
    if (verified) {
      upsert("upchaar:verified_by", "CMHO");
      if (verifiedAt) upsert("upchaar:verified_at", verifiedAt);
      upsert(
        "description",
        `${entityName || "This provider"} is CMHO-verified on UpchaarQ. Book with confidence.`,
      );
      // JSON-LD organization block (idempotent)
      let ld = document.getElementById("upchaar-ld");
      if (!ld) {
        ld = document.createElement("script");
        ld.type = "application/ld+json";
        ld.id = "upchaar-ld";
        document.head.appendChild(ld);
      }
      ld.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "MedicalOrganization",
        name: entityName,
        url,
        hasCredential: {
          "@type": "EducationalOccupationalCredential",
          credentialCategory: "CMHO Registration",
          recognizedBy: { "@type": "Organization", name: "Chief Medical & Health Officer, India" },
        },
      });
    }
    return () => {
      // Clean up JSON-LD on unmount so other pages don't inherit stale schema.
      const ld = document.getElementById("upchaar-ld");
      if (ld) ld.remove();
    };
  }, [verified, entityName, verifiedAt, url]);
}
